import React, { useState, useCallback, useEffect } from 'react';
import { Check, Loader, X } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Z_INDEX_CLASSES } from '@/constants/zIndex';

import { RootState } from '../../../reducers';
import logger from '../../../lib/logger';
import { requestStatusType } from '../../../types';
import {
  setupPayments,
  verifyPaymentMethod,
} from '../../../services/subscriptionServices';
import { config } from '../../../../config';
import { usePricing } from '../../../hooks/usePricing';
import { createOrUpdateSubscription } from '../thunk';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { useBooleanFeature } from '../../../hooks/useFeatures';
import { trackStartTrial } from '../../../lib/tracking';

const { stripePublishableKey } = config;
const stripePromise = loadStripe(stripePublishableKey, {
  betas: ['custom_checkout_beta_2'],
});

// Helper function to get relative dates
const getRelativeDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PaymentSection = ({
  onClose,
  trialDays,
  isPaymentVerificationEnabled,
}: {
  onClose: () => void;
  trialDays: number;
  isPaymentVerificationEnabled: boolean;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [upgradeStatus, setUpgradeStatus] = useState<requestStatusType>('idle');
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { team, settings } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { email } = settings.user;

  // Get pricing based on A/B test
  const { getTrialModalPrice, getYearlyPrice } = usePricing();
  const monthlyPrice = getTrialModalPrice();
  const yearlyPrice = getYearlyPrice(monthlyPrice);

  const handleError = (error: any, userMessage?: string) => {
    logger.error(error);
    setUpgradeStatus('failed');
    // Show user-friendly error message inline
    const message =
      userMessage || error?.message || t('upgrade.freeTrial.errors.generic');
    setErrorMessage(message);
  };

  const handleStartTrial = async () => {
    if (!stripe || !elements) {
      logger.error(t('upgrade.freeTrial.errors.stripeError'));
      return;
    }

    // Clear any previous error
    setErrorMessage(null);
    setUpgradeStatus('pending');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError, submitError.message);
      return;
    }

    try {
      // Collect and verify the payment method
      const { clientSecret } = await setupPayments({
        // userId is implicitly passed with the jwt token
        teamId,
        metadata: {
          toltReferral: window.tolt_referral,
        },
      });

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${config.appBaseUrl}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        throw error;
      }

      // Verify payment method has funds with $1 authorization
      if (isPaymentVerificationEnabled) {
        await verifyPaymentMethod({ teamId });
      }

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: 'GROWTH',
          term: 'annual',
          totalAmount: yearlyPrice,
          // Backend checks this and assigns the number of trial days
          trialLabel: `${trialDays} days free growth annual trial`,
          metadata: {
            toltReferral: window.tolt_referral,
          },
        }),
      );

      if (createOrUpdateSubscription.rejected.match(result as never)) {
        const { message = '' } = (result as any).error || {};
        // Show user-friendly error based on the error type
        const userMessage =
          message.includes('403') || message.includes('forbidden')
            ? t('upgrade.freeTrial.errors.paymentRequired')
            : t('upgrade.freeTrial.errors.generic');
        handleError(new Error(message), userMessage);
        return;
      }

      // Track trial start event
      trackStartTrial({
        plan: 'GROWTH',
        term: 'annual',
        trialLabel: `${trialDays} days free growth annual trial`,
      });

      // Mark trial modal as shown to prevent TrialDialogManager from re-opening it
      localStorage.setItem('trialModalShown', 'true');

      setUpgradeStatus('succeeded');

      // Important: onClose() has a 250ms delay before calling popModal()
      // We need to wait for that to complete before pushing the success modal
      // Otherwise pushModal happens first, then popModal removes it!
      onClose();
      setTimeout(() => {
        dispatch(
          pushModal({
            type: 'upgradeSuccess',
          }),
        );
      }, 300); // Wait for onClose animation (250ms) + buffer
    } catch (error: any) {
      // Extract error message from axios response or use error.message
      const backendMessage = error.response?.data?.message || error.message;

      // Show user-friendly error inline (no toast)
      let userMessage = t('upgrade.freeTrial.errors.generic');

      if (backendMessage?.includes('403') || backendMessage?.includes('forbidden')) {
        userMessage = t('upgrade.freeTrial.errors.paymentRequired');
      } else if (backendMessage) {
        // Use the backend's error message directly (already user-friendly)
        userMessage = backendMessage;
      }

      handleError(error, userMessage);
    }
  };

  return (
    <div className="space-y-6">
      {/* Fixed height container to prevent layout shift */}
      <div className="min-h-[200px] relative">
        {/* Custom skeleton shown until Stripe is ready */}
        {!isPaymentReady && (
          <div className="absolute inset-0 space-y-3 animate-pulse">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-11 bg-muted rounded-lg" />
            <div className="flex gap-3">
              <div className="h-11 flex-1 bg-muted rounded-lg" />
              <div className="h-11 flex-1 bg-muted rounded-lg" />
            </div>
            <div className="h-4 w-20 bg-muted rounded" />
            <div className="h-11 bg-muted rounded-lg" />
          </div>
        )}
        <div className={cn(!isPaymentReady && 'opacity-0')}>
          <PaymentElement
            id="stripe-payment-element"
            onReady={() => setIsPaymentReady(true)}
            onChange={(event) => {
              setIsPaymentComplete(event.complete);
              // Clear error when user starts fixing the form
              if (event.complete && errorMessage) {
                setErrorMessage(null);
              }
            }}
            options={{
              defaultValues: {
                billingDetails: {
                  email,
                },
              },
            }}
          />
        </div>
      </div>

      {/* Inline error display */}
      {errorMessage && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Only show button and pricing once payment method is validated */}
      {isPaymentComplete && (
        <>
          <Button
            type="button"
            variant="default"
            className="w-full h-12 text-base font-bold"
            onClick={handleStartTrial}
            disabled={upgradeStatus === 'pending'}
          >
            {upgradeStatus === 'pending' ? (
              <>
                <Loader className="mr-2 h-5 w-5 animate-spin" />
                {t('upgrade.freeTrial.processing')}
              </>
            ) : (
              t('upgrade.freeTrial.getFreeTrial')
            )}
          </Button>

          <div className="text-center text-sm text-muted-foreground opacity-60">
            Then ${yearlyPrice}/year (${monthlyPrice}/mo) starting{' '}
            {getRelativeDate(trialDays)}
          </div>
        </>
      )}
    </div>
  );
};

interface FreeTrialProps {
  onClose: () => void;
}

const FreeTrial: React.FC<FreeTrialProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { getFeature } = useBooleanFeature();
  const [isClosing, setIsClosing] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  // Detect dark mode for Stripe theme
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  // All users get 7-day trial (feature_7_day_trial enabled for 100%)
  const is7DayTrial = getFeature('feature_7_day_trial');
  const trialDays = is7DayTrial ? 7 : 3;

  // $1 authorization hold - disabled until backend is deployed
  const isPaymentVerificationEnabled = getFeature(
    'feature_payment_verification',
  );

  // Handle close with delay to allow Stripe Elements to clean up properly
  // This prevents "removeChild" DOM errors when the dialog unmounts
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    // First unmount Stripe Elements by hiding content, then close dialog after delay
    setTimeout(() => {
      setIsOpen(false);
      // Additional delay before calling parent onClose to let dialog animation finish
      setTimeout(() => {
        onClose();
      }, 150);
    }, 100);
  }, [isClosing, onClose]);

  const getConversionMessages = (): string[] => {
    return [
      t('upgrade.freeTrial.checks.noCharge'),
      t('upgrade.freeTrial.checks.unlockFeatures'),
      t('upgrade.freeTrial.checks.socialProof'),
    ];
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={() => handleClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            `fixed inset-0 ${Z_INDEX_CLASSES.dialogOverlay} bg-white/40 dark:bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            `fixed left-[50%] top-[50%] ${Z_INDEX_CLASSES.dialog} grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white dark:bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg`,
            'max-h-[90vh] overflow-y-auto',
          )}
          data-testid="free-trial-modal"
          onInteractOutside={(e) => {
            // Prevent closing when clicking outside - Stripe Link may open popups/overlays
            // that are technically "outside" the dialog but part of the payment flow
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            // Prevent default close behavior, handle manually
            e.preventDefault();
            handleClose();
          }}
        >
          {/* Header Section */}
          <div className="flex flex-col space-y-1.5 text-center sm:text-left pb-6">
            <DialogPrimitive.Title className="text-2xl font-bold">
              {t('upgrade.freeTrial.title', { days: trialDays })}
            </DialogPrimitive.Title>
          </div>

          {/* Content Section */}
          <div className="space-y-6">
            {/* Conversion Messaging */}
            <div className="space-y-4">
              {getConversionMessages().map((text, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-base leading-relaxed flex-1">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* Payment section - unmount Stripe Elements first when closing */}
            {!isClosing && (
              <Elements
                key={isDarkMode ? 'dark' : 'light'}
                stripe={stripePromise}
                options={{
                  mode: 'setup',
                  currency: 'usd',
                  setupFutureUsage: 'off_session',
                  loader: 'always',
                  appearance: {
                    theme: isDarkMode ? 'night' : 'stripe',
                    variables: {
                      colorPrimary: '#1E90FF',
                      colorBackground: isDarkMode ? '#1A1A1A' : '#FFFFFF',
                      colorText: isDarkMode ? '#E6E6E6' : '#363739',
                      fontFamily:
                        'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                      borderRadius: '8px',
                      // Ensure white text on primary-colored buttons
                      accessibleColorOnColorPrimary: '#FFFFFF',
                    },
                    rules: {
                      // Style Link "use my card" and other action buttons with primary blue
                      '.Action': {
                        backgroundColor: '#1E90FF',
                        color: '#FFFFFF',
                      },
                      '.Action:hover': {
                        backgroundColor: '#1A7FE8',
                      },
                      '.BlockAction': {
                        backgroundColor: '#1E90FF',
                        color: '#FFFFFF',
                      },
                      '.BlockAction:hover': {
                        backgroundColor: '#1A7FE8',
                      },
                    },
                  },
                }}
              >
                <PaymentSection
                  onClose={handleClose}
                  trialDays={trialDays}
                  isPaymentVerificationEnabled={isPaymentVerificationEnabled}
                />
              </Elements>
            )}

            {/* $1 authorization notice - only shown when feature is enabled */}
            {isPaymentVerificationEnabled && (
              <p className="text-sm text-muted-foreground">
                {t('upgrade.freeTrial.checks.cardVerification')}
              </p>
            )}
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default FreeTrial;
